import { ogImageMeta } from "@/lib/og-images";
import { createFileRoute, Link } from "@tanstack/react-router";
import logo from "@/assets/thevibingco_logo.svg.asset.json";
import { MagneticButton } from "@/components/magnetic-button";
import { TalkToJonButton } from "@/components/talk-to-jon";
import { SiteFooter } from "@/components/site-footer";
import { PricingPlans } from "@/components/pricing-plans";

export const FAQS = [
  {
    q: "What do the 14 days actually include?",
    a: "Everything between the idea and a live product. Product strategy and a locked scope on day 1, UI/UX design and a design system, the whole build with accounts, data, payments and integrations, a performance, accessibility and SEO pass, and launch on your domain on day 14. Then a handover of the code, the design files and the domain.",
  },
  {
    q: "How can you ship in 14 days?",
    a: "Because the people who design it also build it, and because the scope is locked on day 1. We build the first stretch with Lovable, Cursor and Claude Code and engineer the parts that matter (security, payments, performance, edge cases) by hand. Bigger scopes continue after launch as two-week sprints.",
  },
  {
    q: "How much does it cost?",
    a: "€35,000, fixed. One price for strategy, design, the whole build and the handover. No hourly billing, no change orders. Brand identity, launch website, motion, film and decks are scoped on the call.",
  },
  {
    q: "Who is this for?",
    a: "Companies that need to prove an idea fast and scale it faster: funded startups, scale-ups launching a new product line, and corporate teams spinning up a venture. Websites and web apps alike. You stay in charge of the decisions; we handle design and engineering.",
  },
  {
    q: "What happens after launch?",
    a: "You own everything. Most clients keep the studio for the work around the product: brand identity, a launch website, motion and 3D, a product film, pitch and sales decks. Some keep the team on two-week sprints. Some come back when something breaks; that is the rescue service.",
  },
];

export const Route = createFileRoute("/services/mvp-development")({
  head: () => ({
    meta: [
      { title: "MVP Development Services · Idea to Live Product in 14 Days | Vibing Co" },
      {
        name: "description",
        content:
          "MVP development services for companies that need to prove it fast. Full-stack designers design and build your website or web app to a studio standard, live in 14 days for a fixed €35,000. You own the code.",
      },
      { property: "og:title", content: "MVP development services · Live product in 14 days | Vibing Co" },
      ...ogImageMeta("services-mvp-development", "Share card: idea to live product in 14 days"),
      {
        property: "og:description",
        content:
          "Studio-grade UI/UX and the whole build, live in 14 days. €35,000 fixed, one launch date, you own everything.",
      },
      { property: "og:url", content: "https://vibing.inc/services/mvp-development" },
    ],
    links: [{ rel: "canonical", href: "https://vibing.inc/services/mvp-development" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          name: "MVP development: idea to live product in 14 days",
          serviceType: "MVP development services · product design and development studio",
          offers: {
            "@type": "Offer",
            price: "35000",
            priceCurrency: "EUR",
            description: "Idea to live product in 14 days. Fixed price, you own everything.",
          },
          provider: { "@type": "Organization", name: "the Vibing Company", url: "https://vibing.inc/" },
          areaServed: "Worldwide",
          url: "https://vibing.inc/services/mvp-development",
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQS.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
    ],
  }),
  component: MVP,
});

function MVP() {
  return (
    <div className="min-h-dvh bg-background text-foreground">
      <main id="main" tabIndex={-1}>
      <Hero />
      <Stack />
      <Timeline />
      <PricingPlans />
      <FAQ />
      <CTA />
      </main>
      <SiteFooter />
    </div>
  );
}


function Hero() {
  return (
    <section aria-labelledby="mvp-h" className="relative mx-auto max-w-[1400px] px-6 md:px-10 pt-[clamp(9rem,18vh,13rem)] pb-16 md:pb-24">
      <h1 id="mvp-h" className="mt-8 font-display tracking-[-0.035em] leading-[1.02] max-w-[16ch]" style={{ fontSize: "clamp(2rem, 4.4vw, 3.5rem)" }}>
        Your product, designed and live in 14 days.
      </h1>
      <p className="mt-10 max-w-[60ch] text-xl md:text-2xl leading-snug text-foreground">
        We are the external product team for companies that need to prove it fast. Full-stack designers take your brief through product strategy, UI/UX and the whole build, and hand you a live website or web app on day 14. €35,000, fixed.
      </p>
      <div className="mt-10 md:mt-14 flex flex-wrap gap-3">
        <TalkToJonButton variant="dark" immersive />
        <MagneticButton href="/work" variant="outline-dark">
          See our work
        </MagneticButton>
      </div>
    </section>
  );
}

const STACK = [
  { k: "01", t: "Scope locked on day 1", b: "One call, one written scope, one design direction. By the end of the day you know exactly what goes live on day 14." },
  { k: "02", t: "Studio-grade design", b: "A design system built for growth, not a template. It looks like the product you want to be compared with." },
  { k: "03", t: "Engineered where it counts", b: "Payments that reconcile. Auth that holds. Pages that load fast on a bad connection. The parts that kill amateur builds get senior hands." },
  { k: "04", t: "A handover you can build on", b: "The code, the domain, the database and the design files, with a written walkthrough. Keep building yourself, or keep us." },
];

function Stack() {
  return (
    <section aria-labelledby="stack-h" className="relative border-t border-border/60 mx-auto max-w-[1400px] px-6 md:px-10 py-20 md:py-32">
      <h2 id="stack-h" className="fluid-h2 font-display max-w-[16ch]">A real product, not a prototype.</h2>
      <ul className="mt-14 grid md:grid-cols-2 gap-5">
        {STACK.map((s) => (
          <li key={s.k} className="bg-background p-8 md:p-12">
            <div className="flex items-baseline gap-5">
              <span className="font-display text-electric leading-none" style={{ fontSize: "clamp(2.5rem, 4vw, 4rem)" }}>{s.k}</span>
              <h3 className="font-display tracking-[-0.03em] leading-[1.02]" style={{ fontSize: "clamp(1.5rem, 2.2vw, 2.25rem)" }}>{s.t}</h3>
            </div>
            <p className="mt-6 text-base md:text-lg text-muted-foreground leading-snug max-w-[44ch]">{s.b}</p>
          </li>
        ))}
      </ul>
    </section>
  );
}

const TIMELINE = [
  { k: "Day 1", v: "Kickoff, strategy, scope locked. The design direction is set before the day ends." },
  { k: "Days 2 to 7", v: "Design system and core screens, with the build starting underneath. By Friday you can click through it." },
  { k: "Days 8 to 14", v: "Accounts, data, payments, integrations. Performance, accessibility and SEO pass. Live on day 14." },
];

function Timeline() {
  return (
    <section aria-labelledby="t-h" className="relative border-t border-border/60 bg-secondary/40">
      <div className="mx-auto max-w-[1400px] px-6 md:px-10 py-20 md:py-32 grid md:grid-cols-12 gap-10">
        <div className="md:col-span-4">
          <h2 id="t-h" className="fluid-h2 font-display max-w-[14ch]">Fourteen days. Not a quarter.</h2>
          <p className="mt-6 text-muted-foreground max-w-prose">
            Not three months. Not a discovery phase that turns into one. The clock starts the day the scope is signed.
          </p>
        </div>
        <ul className="md:col-span-7 md:col-start-6 border-t border-foreground/20">
          {TIMELINE.map((s) => (
            <li key={s.k} className="grid grid-cols-[100px_1fr] md:grid-cols-[140px_1fr] gap-6 items-baseline py-6 border-b border-foreground/20">
              <span className="font-display tracking-[-0.03em]" style={{ fontSize: "clamp(1.5rem, 2.2vw, 2.25rem)" }}>{s.k}</span>
              <span className="text-base md:text-lg leading-snug">{s.v}</span>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

function FAQ() {
  return (
    <section aria-labelledby="faq-h" className="relative border-t border-border/60 mx-auto max-w-[1400px] px-6 md:px-10 py-20 md:py-32">
      <h2 id="faq-h" className="fluid-h2 font-display max-w-[18ch]">MVP development, answered.</h2>
      <dl className="mt-14 border-t border-border">
        {FAQS.map((f) => (
          <div key={f.q} className="grid md:grid-cols-12 gap-6 py-8 border-b border-border">
            <dt className="md:col-span-5 font-display text-xl md:text-2xl tracking-[-0.02em] leading-snug">{f.q}</dt>
            <dd className="md:col-span-6 md:col-start-7 text-base md:text-lg text-muted-foreground leading-relaxed">{f.a}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

function CTA() {
  return (
    <section aria-labelledby="cta-h" className="relative border-t border-border/60 bg-foreground text-background">
      <div className="mx-auto max-w-[1400px] px-6 md:px-10 py-24 md:py-40 grid md:grid-cols-12 gap-10 items-end">
        <div className="md:col-span-7">
          <h2 id="cta-h" className="font-display tracking-[-0.035em] leading-[1.02]" style={{ fontSize: "clamp(2rem, 4.4vw, 3.5rem)" }}>
            Tell us the idea.
          </h2>
          <p className="mt-8 text-lg md:text-xl text-background/70 max-w-[52ch] leading-snug">
            One paragraph on what you want to ship and when it needs to be live. Talk it through with Jon.
          </p>
        </div>
        <div className="md:col-span-4 md:col-start-9">
          <a href="https://cal.com/vibinc/30min" target="_blank" rel="noopener noreferrer" className="block group">
            <span className="mt-3 block font-display text-electric-bright break-all" style={{ fontSize: "clamp(1.75rem, 3vw, 2.75rem)" }}>
              Talk to Jon<span aria-hidden className="inline-block ml-2 transition-transform group-hover:translate-x-2">↗</span>
            </span>
          </a>
        </div>
      </div>
    </section>
  );
}
